import { cn } from '@/lib/utils'
import type { PanelArtProps } from './panel-art-props'

/**
 * Heatmap panel art: a matrix, not a treemap. Six equal columns by four rows of
 * cells with captions down the left and across the top, each cell shaded by how
 * that row and column crossed.
 *
 * Cells are scored from -1 to 1 the same way the calendar heatmap scores its
 * days: positive shades green at that alpha, negative red, zero stays empty.
 */

const COLUMN_X = [37, 56, 75, 94, 113, 132]
const ROW_Y = [22, 39, 56, 73]

/** One row per matrix row, one score per column. */
const CELLS: number[][] = [
  [0.55, 0.3, -0.25, 0.7, 0.4, 0.2],
  [0.25, -0.45, 0.35, 0.5, -0.2, 0.6],
  [-0.35, 0.4, 0.65, -0.3, 0.45, 0.3],
  [0.4, 0.2, -0.5, 0.3, 0.6, -0.25],
]

function cellFill(score: number): string {
  if (score === 0) return 'hsl(var(--foreground) / 0.07)'
  if (score > 0) return `hsl(var(--positive) / ${score})`
  return `hsl(var(--negative) / ${-score})`
}

export function HistoryHeatmapArt({ className }: PanelArtProps) {
  return (
    <svg
      viewBox="0 0 160 100"
      preserveAspectRatio="xMidYMid meet"
      aria-hidden="true"
      focusable="false"
      className={cn('h-full w-full', className)}
    >
      {COLUMN_X.map((x) => (
        <rect
          key={x}
          x={x + 2}
          y={12}
          width={12}
          height={4}
          rx={2}
          fill="hsl(var(--foreground) / 0.2)"
        />
      ))}
      {ROW_Y.map((y) => (
        <rect
          key={y}
          x={12}
          y={y + 5}
          width={20}
          height={5}
          rx={2}
          fill="hsl(var(--foreground) / 0.22)"
        />
      ))}
      {CELLS.map((row, rowIndex) =>
        row.map((score, colIndex) => (
          <rect
            key={`${rowIndex}-${colIndex}`}
            x={COLUMN_X[colIndex]}
            y={ROW_Y[rowIndex]}
            width={16}
            height={15}
            rx={2}
            fill={cellFill(score)}
          />
        )),
      )}
    </svg>
  )
}
